/**
 * Resolve a catalog entity to the config.json it maps to, so the entity "Configuration" card
 * can fetch + preview it. Pure + isomorphic so it's unit-testable and shared.
 *
 * Three kinds of entity map to a config:
 *  1. API / Step Function entities — `name` is the config directory and `github.com/project-slug`
 *     points at the infra repo. path = basePath/<name>.
 *  2. Lambda-in-an-API entities — one Component per Lambda, whose `github.com/project-slug` points at
 *     the *lambdas* source repo (not the infra repo), so the slug can't resolve the config. These
 *     carry `aws.amazon.com/lambda-function-name` (the config.json key), `spec.system` (the infra
 *     component, e.g. `awb`), and `spec.providesApis: [<api-dir>]`. We resolve the infra repo from
 *     `spec.system` and focus the single lambda within that API's config.
 *  3. Standalone lambda entities (phx-lambda-v1) — one Component per lambda dir in the shared
 *     `phoenix_infra-lambdas` monorepo. The slug points at that monorepo, whose `matchTarget`
 *     returns null (the component lives in the path, not the repo name), and there is no
 *     lambda-function-name annotation. We resolve the infra component from `spec.system` with a
 *     `lambda` target; `name` is the lambda dir (item). path = basePath/<name>/config.json.
 */

import { getTargetFromRepo, resolveConfigTarget } from './projects';
import type { ConfigTargetType } from './projects';

const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';
const LAMBDA_FUNCTION_NAME_ANNOTATION = 'aws.amazon.com/lambda-function-name';
const DATADOG_SERVICE_NAME_ANNOTATION = 'datadoghq.com/service-name';

/** Minimal entity shape this resolver needs (subset of the Backstage Entity). */
export interface EntityLike {
  metadata: { name: string; annotations?: Record<string, string> };
  spec?: Record<string, unknown>;
}

export interface EntityConfigPlan {
  type: ConfigTargetType;
  owner: string;
  repo: string;
  basePath: string;
  branch: string;
  /** infra component (team), e.g. `awb` */
  component: string;
  /** config directory under basePath (the API / Step Function dir name) */
  item: string;
  /** when set, the entity is a single Lambda inside `item`'s config — focus just this key */
  focusLambda?: string;
}

const firstString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value) return value;
  if (Array.isArray(value)) {
    const s = value.find(v => typeof v === 'string' && v);
    return typeof s === 'string' ? s : undefined;
  }
  return undefined;
};

/**
 * Map an entity to its config target, or null if the entity isn't backed by a known config repo.
 */
export function resolveEntityConfigPlan(entity: EntityLike): EntityConfigPlan | null {
  const ann = entity.metadata.annotations ?? {};

  // Lambda entity: resolve the infra repo from spec.system, focus the single function.
  const lambdaName = ann[LAMBDA_FUNCTION_NAME_ANNOTATION];
  if (lambdaName) {
    const component = firstString(entity.spec?.system);
    const apiItem = firstString(entity.spec?.providesApis) ?? ann[DATADOG_SERVICE_NAME_ANNOTATION];
    if (!component || !apiItem) return null;

    // Lambdas only exist under API configs (never step functions' managed_lambdas here).
    const resolved = resolveConfigTarget(component, 'api');
    if (!resolved) return null;

    return {
      type: 'api',
      owner: resolved.owner,
      repo: resolved.repo,
      basePath: resolved.basePath,
      branch: resolved.defaultBranch,
      component,
      item: apiItem,
      focusLambda: lambdaName,
    };
  }

  const repo = ann[PROJECT_SLUG_ANNOTATION]?.split('/')[1];

  // Standalone lambda entity (phx-lambda-v1): slug is the phoenix_infra-lambdas monorepo, whose
  // matchTarget can't recover the component from the repo name. Resolve the infra component from
  // spec.system with a `lambda` target; the entity name is the lambda dir. Guarded on the slug repo
  // matching the resolved lambda repo so only genuine lambda-monorepo components take this branch.
  const system = firstString(entity.spec?.system);
  if (repo && system) {
    const lambdaTarget = resolveConfigTarget(system, 'lambda');
    if (lambdaTarget && lambdaTarget.repo === repo) {
      return {
        type: 'lambda',
        owner: lambdaTarget.owner,
        repo: lambdaTarget.repo,
        basePath: lambdaTarget.basePath,
        branch: lambdaTarget.defaultBranch,
        component: system,
        item: entity.metadata.name,
      };
    }
  }

  // API / Step Function entity: name is the config dir, slug points at the infra repo.
  const target = repo ? getTargetFromRepo(repo) : null;
  if (!target) return null;

  const resolved = resolveConfigTarget(target.componentName, target.type);
  if (!resolved) return null;

  return {
    type: target.type,
    owner: resolved.owner,
    repo: resolved.repo,
    basePath: resolved.basePath,
    branch: resolved.defaultBranch,
    component: target.componentName,
    item: entity.metadata.name,
  };
}
